import { useState, useEffect, useCallback } from 'react';
import { analyticsApi } from '@/api/analytics';

/**
 * QR Overview data. ``params`` carries period, granularity, qr_code_id and
 * the filter card (camelCase is fine: analyticsApi converts to snake_case).
 */
export function useAnalytics(params = {}) {
  const [overview, setOverview] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [deviceBreakdown, setDeviceBreakdown] = useState([]);
  const [countryBreakdown, setCountryBreakdown] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const key = JSON.stringify(params);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { granularity, ...rest } = JSON.parse(key);

    try {
      const [overviewRes, timeSeriesRes, deviceRes, countryRes, recentRes, funnelRes] = await Promise.all([
        analyticsApi.getOverview(rest),
        analyticsApi.getScansOverTime({ ...rest, granularity }),
        analyticsApi.getBreakdown('by-device', rest),
        analyticsApi.getBreakdown('by-country', rest),
        analyticsApi.getRecentScans({ ...rest, period: undefined, limit: 10 }),
        analyticsApi.getFunnel(rest),
      ]);

      setOverview(overviewRes.data);
      setTimeSeries(timeSeriesRes.data?.data_points || []);
      setDeviceBreakdown(deviceRes.data?.breakdown || []);
      setCountryBreakdown(countryRes.data?.breakdown || []);
      setRecentScans(recentRes.data?.scans || []);
      setFunnel(funnelRes.data || null);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    overview,
    timeSeries,
    deviceBreakdown,
    countryBreakdown,
    recentScans,
    funnel,
    loading,
    error,
    refetch: fetchAnalytics,
  };
}

export default useAnalytics;
